import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, HttpError } from "../api/rest";
import type { FollowMode } from "../api/types";
import { useFollowMode } from "../hooks/useSignalFeed";
import { useStatus } from "../hooks/useStatus";
import { FOLLOW_MODES, modeAllowed, modeHint, modeLabel, raisesRisk } from "../lib/signals";
import { SubsystemAccountSelect } from "./SubsystemAccountSelect";

/**
 * Переключатель режима копирования.
 *
 * # Асимметрия подтверждения
 *
 * Понизить режим — один клик, поднять — два, и второй шаг сам себя отменяет через
 * пять секунд. Та же идиома, что у аварийного стопа, и по той же причине: ошибка
 * в сторону «меньше торговли» стоит пропущенных сигналов, ошибка в другую сторону
 * стоит денег.
 *
 * # Потолок окружения виден, а не молчит
 *
 * Действующий режим — строгий МИНИМУМ выставленного здесь и FOLLOW_MODE трейдера.
 * Кнопка выше потолка выключена с причиной: выбор, который заведомо ничего не
 * изменит, не должен срабатывать в пустоту. Снимается потолок правкой .env и
 * перезапуском трейдера — ровно как у стопа.
 */
const CONFIRM_MS = 5000;

export function FollowModeSwitch() {
  const mode = useFollowMode();
  const status = useStatus();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<FollowMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Подтверждение само себя отменяет: карточка режима не должна оставаться
  // «взведённой» после того, как оператор отвлёкся.
  useEffect(() => {
    if (!pending) return;
    const id = window.setTimeout(() => setPending(null), CONFIRM_MS);
    return () => window.clearTimeout(id);
  }, [pending]);

  const current = mode.data?.follow_mode.mode ?? null;
  const set = mode.data?.follow_mode.set ?? false;
  // Потолок приходит нотой трейдера: web_api его не знает и знать не должен —
  // это решение времени выкатки, живущее в окружении торгового процесса.
  const ceiling = status.data?.follow_mode_env ?? null;

  async function apply(next: FollowMode) {
    if (raisesRisk(current, next) && pending !== next) {
      setPending(next);
      return;
    }
    setPending(null);
    setError(null);
    try {
      await api.setFollowMode(next);
      void queryClient.invalidateQueries({ queryKey: ["follow-mode"] });
    } catch (err) {
      setError(err instanceof HttpError ? err.message : "не удалось записать режим");
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {FOLLOW_MODES.map((m) => {
          const allowed = modeAllowed(m, ceiling);
          const active = set && current === m;
          const confirming = pending === m;
          return (
            <button
              key={m}
              type="button"
              disabled={!allowed}
              onClick={() => void apply(m)}
              title={
                allowed
                  ? modeHint(m)
                  : ceiling
                    ? `выше потолка FOLLOW_MODE=${ceiling}`
                    : "потолок неизвестен: трейдер не отвечает либо копирования у него нет"
              }
              className={`rounded border px-3 py-1 text-xs disabled:opacity-40 ${
                active
                  ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                  : "border-[var(--color-border)]"
              }`}
            >
              {confirming ? "точно?" : modeLabel(m)}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-[var(--color-muted)]">
        {set ? (
          <>
            Выставлено: <b>{modeLabel(current ?? "off")}</b> — {modeHint(current ?? "off")}.
          </>
        ) : (
          <>Из интерфейса режим не выставляли: действует потолок окружения трейдера.</>
        )}{" "}
        {ceiling ? (
          <>
            Потолок <b>{modeLabel(ceiling)}</b> задан переменной <code>FOLLOW_MODE</code> и
            кнопкой не снимается — правьте <code>.env</code> и перезапускайте трейдер.
          </>
        ) : (
          <>
            Потолок неизвестен: трейдер не отвечает либо копирования у него нет вовсе.
            Поднять режим кнопкой нельзя — она обещала бы копирование, которого не будет.
          </>
        )}
      </p>

      {/* Копирование торгует от одного аккаунта (Д-6); не назначен — режим
          «авто» не поставит ни одной заявки. */}
      <SubsystemAccountSelect subsystem="follow" />

      {error && <p className="text-xs text-[var(--color-sell)]">{error}</p>}
    </div>
  );
}
